import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui/checkbox';
import { ChoiceSelect } from '@/components/ui/choice-select';
import { Input } from '@/components/ui/input';
import { claimedKm, formatKm, parseKm } from './calc';
import type { ListedVehicle } from './api';
import type { TripField, TripForm } from './trip-form';

type Props = {
  readonly form: TripForm;
  readonly set: (patch: Partial<TripForm>) => void;
  readonly vehicles: readonly ListedVehicle[];
  readonly invalid: ReadonlySet<TripField>;
  /** The «Percorsi usuali» and «Aggiungi veicolo» controls, placed beside their fields. */
  readonly routesControl: ReactNode;
  readonly vehicleControl: ReactNode;
};

function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  readonly label: string;
  readonly htmlFor: string;
  readonly error?: string | undefined;
  readonly children: ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label htmlFor={htmlFor} className="text-sm font-semibold">
        {label}
      </label>
      {children}
      {error ? (
        <p className="text-destructive text-sm" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** «Nuovo viaggio»'s fields (spec §2.1), one column on a phone, two from 620 px of shell width. */
export function TripFields(props: Props) {
  const { form, set, invalid } = props;
  const { t } = useTranslation('expenses');
  const uid = useId();
  const err = (key: TripField) => (invalid.has(key) ? t(`mileage.trip.errors.${key}`) : undefined);
  return (
    <div className="grid gap-4 @min-[620px]/shell:grid-cols-2">
      <Field label={t('mileage.trip.date')} htmlFor={`${uid}-date`} error={err('date')}>
        <Input
          id={`${uid}-date`}
          type="date"
          value={form.date}
          aria-invalid={invalid.has('date')}
          onChange={(event) => set({ date: event.target.value })}
        />
      </Field>
      <label className="flex min-h-11 items-center gap-2 self-end text-sm font-semibold">
        <Checkbox
          checked={form.roundTrip}
          onChange={(event) => set({ roundTrip: event.target.checked })}
        />
        {t('mileage.trip.roundTrip')}
      </label>
      <Field label={t('mileage.trip.from')} htmlFor={`${uid}-from`} error={err('from')}>
        <Input
          id={`${uid}-from`}
          value={form.from}
          maxLength={200}
          aria-invalid={invalid.has('from')}
          onChange={(event) => set({ from: event.target.value })}
        />
      </Field>
      <Field label={t('mileage.trip.to')} htmlFor={`${uid}-to`} error={err('to')}>
        <Input
          id={`${uid}-to`}
          value={form.to}
          maxLength={200}
          aria-invalid={invalid.has('to')}
          onChange={(event) => set({ to: event.target.value })}
        />
      </Field>
      <div className="@min-[620px]/shell:col-span-2">{props.routesControl}</div>
      <KmVehiclePurpose {...props} />
    </div>
  );
}

/** The second half: km, vehicle and motivo. */
function KmVehiclePurpose(props: Props) {
  const { form, set, invalid } = props;
  const { t } = useTranslation('expenses');
  const uid = useId();
  const err = (key: TripField) => (invalid.has(key) ? t(`mileage.trip.errors.${key}`) : undefined);
  const km = parseKm(form.km);
  return (
    <>
      <Field label={t('mileage.trip.km')} htmlFor={`${uid}-km`} error={err('km')}>
        <Input
          id={`${uid}-km`}
          inputMode="decimal"
          autoComplete="off"
          value={form.km}
          aria-invalid={invalid.has('km')}
          onChange={(event) => set({ km: event.target.value })}
        />
        <p className="text-muted-foreground text-sm">
          {km !== null && form.roundTrip
            ? t('mileage.trip.kmRound', { km: formatKm(claimedKm(km, true)) })
            : t('mileage.trip.kmHint')}
        </p>
      </Field>
      <Field label={t('mileage.trip.vehicle')} htmlFor={`${uid}-vehicle`} error={err('vehicleId')}>
        <ChoiceSelect
          id={`${uid}-vehicle`}
          value={form.vehicleId}
          invalid={invalid.has('vehicleId')}
          onChange={(vehicleId) => set({ vehicleId })}
          choices={[
            { value: '', label: t('mileage.trip.vehicleChoose') },
            ...props.vehicles.map((vehicle) => ({
              value: vehicle.id,
              label: `${vehicle.label}${vehicle.plate ? ` · ${vehicle.plate}` : ''}`,
            })),
          ]}
        />
        {props.vehicleControl}
      </Field>
      <div className="@min-[620px]/shell:col-span-2">
        <Field label={t('mileage.trip.purpose')} htmlFor={`${uid}-purpose`} error={err('purpose')}>
          <Input
            id={`${uid}-purpose`}
            value={form.purpose}
            maxLength={500}
            aria-invalid={invalid.has('purpose')}
            onChange={(event) => set({ purpose: event.target.value })}
          />
        </Field>
      </div>
    </>
  );
}
